import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { API_ENDPOINTS, API_BASE_URL } from '../../utils/GlobalAPI';
// Removed Twilio OTP; using SMS OTP via backend /api/sms-otp
import { sendRegistrationConfirmation } from '../../utils/emailService';

// Valid promotion code for free registration (no payment, no phone verification)
const VALID_PROMO_CODE = 'CREATORFREE';

const RegisterInfluencer = ({ onClose }) => {
  const [form, setForm] = useState({
    name: '',
    phone: '',
    email: '',
    instagram: '',
    facebook: '',
    twitter: '',
    screenshot: null,
    transactionId: '',
    influencerType: 'Featured Influencer',
    password: '',
    confirmPassword: '',
    promotionCode: ''
  });
  
  const [preview, setPreview] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState('razorpay'); // 'razorpay' | 'promotion' (manual disabled)
  const [promoCodeValid, setPromoCodeValid] = useState(false);
  const [promoCodeTouched, setPromoCodeTouched] = useState(false);
  const [passwordError, setPasswordError] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [paymentError, setPaymentError] = useState(null);
  const [razorpayOrderId, setRazorpayOrderId] = useState(null);
  const [razorpayKeyId, setRazorpayKeyId] = useState(null);
  
  // OTP Verification States
  const [showOTPVerification, setShowOTPVerification] = useState(false);
  const [phoneVerified, setPhoneVerified] = useState(false);
  const [verificationData, setVerificationData] = useState(null);
  const [otp, setOtp] = useState('');
  const [otpLoading, setOtpLoading] = useState(false);
  const [otpMessage, setOtpMessage] = useState('');

  const price = 199; // Single registration fee; featured placement is handled by sales team

  // Load Razorpay checkout script
  useEffect(() => {
    const script = document.createElement('script');
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.async = true;
    document.body.appendChild(script);

    // Fetch Razorpay Key ID from backend or env
    const fetchRazorpayKey = async () => {
      try {
        // First try environment variable
        const envKeyId = process.env.REACT_APP_RAZORPAY_KEY_ID;
        if (envKeyId) {
          setRazorpayKeyId(envKeyId);
          return;
        }
        
        // Fallback: fetch from backend
        const response = await axios.get(`${API_BASE_URL}/api/razorpay/key-id`);
        if (response.data.success && response.data.key_id) {
          setRazorpayKeyId(response.data.key_id);
        }
      } catch (err) {
        console.warn('Could not fetch Razorpay key ID:', err);
        // If backend fetch fails, try env variable as fallback
        const envKeyId = process.env.REACT_APP_RAZORPAY_KEY_ID;
        if (envKeyId) {
          setRazorpayKeyId(envKeyId);
        }
      }
    };
    fetchRazorpayKey();

    return () => {
      // Cleanup script on unmount
      const existingScript = document.querySelector('script[src="https://checkout.razorpay.com/v1/checkout.js"]');
      if (existingScript) {
        document.body.removeChild(existingScript);
      }
    };
  }, []);

  // Razorpay Payment Handler
  const handleRazorpayPayment = async () => {
    if (!form.name || !form.email || !form.phone) {
      setPaymentError('Please fill in name, email, and phone before making payment');
      return;
    }

    setPaymentError(null);
    setPaymentLoading(true);

    try {
      // Step 1: Create order on backend
      const orderResponse = await axios.post(`${API_BASE_URL}/api/razorpay/create-order`, {
        amount: price,
        currency: 'INR',
        receipt: `inf_${Date.now()}_${Math.floor(Math.random() * 10000)}`,
        notes: {
          name: form.name,
          email: form.email,
          phone: form.phone,
          instagram: form.instagram,
          facebook: form.facebook,
          twitter: form.twitter,
          influencerType: form.influencerType
        }
      });

      if (!orderResponse.data.success || !orderResponse.data.order) {
        throw new Error('Failed to create payment order');
      }

      const order = orderResponse.data.order;
      setRazorpayOrderId(order.id);

      // Get Razorpay Key ID (from env or backend)
      const keyId = razorpayKeyId || process.env.REACT_APP_RAZORPAY_KEY_ID;
      if (!keyId) {
        throw new Error('Razorpay Key ID not configured');
      }

      // Step 2: Initialize Razorpay checkout
      const options = {
        key: keyId,
        amount: order.amount, // Amount in paise
        currency: order.currency,
        name: 'Cyborgsapient',
        description: `${form.influencerType} Registration - ₹${price}`,
        order_id: order.id,
        prefill: {
          name: form.name,
          email: form.email,
          contact: form.phone
        },
        notes: {
          influencerType: form.influencerType,
          instagram: form.instagram,
          facebook: form.facebook,
          twitter: form.twitter
        },
        theme: {
          color: '#3399cc'
        },
        handler: async function (response) {
          // Step 3: Payment successful - verify signature
          try {
            const verifyResponse = await axios.post(`${API_BASE_URL}/api/razorpay/verify-payment`, {
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature
            });

            if (verifyResponse.data.success && verifyResponse.data.verified) {
              // Payment verified successfully
              setForm(f => ({ 
                ...f, 
                transactionId: response.razorpay_payment_id 
              }));
              setPaymentError(null);
              setPaymentLoading(false);
              // Don't set success here - let user submit the form
              // The transaction ID is now set, so form can be submitted
            } else {
              throw new Error('Payment verification failed');
            }
          } catch (verifyErr) {
            console.error('Payment verification error:', verifyErr);
            setPaymentError('Payment verification failed. Please contact support.');
            setPaymentLoading(false);
          }
        },
        modal: {
          ondismiss: function() {
            setPaymentLoading(false);
            setPaymentError('Payment cancelled');
          }
        }
      };

      const rzp = new window.Razorpay(options);
      rzp.open();
    } catch (err) {
      console.error('Razorpay payment error:', err);
      setPaymentError(err.response?.data?.error || err.message || 'Failed to initiate payment');
      setPaymentLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, files } = e.target;
    if (name === 'screenshot') {
      setForm(f => ({ ...f, screenshot: files[0] }));
      setPreview(files[0] ? URL.createObjectURL(files[0]) : null);
    } else {
      setForm(f => ({ ...f, [name]: value }));
      if (name === 'password' || name === 'confirmPassword') {
        setPasswordError('');
      }
      if (name === 'promotionCode') {
        const trimmed = value.trim().toUpperCase();
        setPromoCodeValid(trimmed === VALID_PROMO_CODE.toUpperCase());
        setPromoCodeTouched(true);
      }
      // Reset phone verification if phone number changes
      if (name === 'phone' && phoneVerified) {
        setPhoneVerified(false);
        setVerificationData(null);
        setShowOTPVerification(false);
      }
    }
  };

  // OTP Verification Handlers
  const handleOTPVerified = () => {
    const data = { phone: `+91${form.phone}`, verified: true, method: 'sms' };
    setPhoneVerified(true);
    setVerificationData(data);
    setShowOTPVerification(false);
    setError(null);
  };

  const handleVerifyPhone = () => {
    if (!form.phone) {
      setError('Please enter your phone number first');
      return;
    }
    
    if (form.phone.length < 10) {
      setError('Please enter a valid 10-digit phone number');
      return;
    }
    
    setOtp('');
    setOtpMessage('');
    setShowOTPVerification(true);
    setError(null);
  };

  const smsOtpBase = `${API_BASE_URL}/api/sms-otp`;
  const sendSmsOtp = async () => {
    setOtpLoading(true);
    setOtpMessage('');
    try {
      const phone = `+91${form.phone}`;
      const resp = await axios.post(`${smsOtpBase}/send`, { phone });
      if (!resp.data?.success) throw new Error(resp.data?.error || 'Failed to send OTP');
      setOtpMessage('OTP sent. Please check your phone.');
    } catch (e) {
      setOtpMessage(e.response?.data?.error || e.message || 'Failed to send OTP');
    } finally {
      setOtpLoading(false);
    }
  };

  const verifySmsOtp = async () => {
    if (otp.length !== 6) {
      setOtpMessage('Enter 6-digit code');
      return;
    }
    setOtpLoading(true);
    setOtpMessage('');
    try {
      const phone = `+91${form.phone}`;
      const resp = await axios.post(`${smsOtpBase}/verify`, { phone, code: otp });
      if (!resp.data?.success) throw new Error(resp.data?.error || 'Invalid code');
      handleOTPVerified();
    } catch (e) {
      setOtpMessage(e.response?.data?.error || e.message || 'Verification failed');
    } finally {
      setOtpLoading(false);
    }
  };

  const validateForm = () => {
    // Promotion path: no payment or OTP verification, but valid promo code required
    if (paymentMethod === 'promotion') {
      if (!promoCodeValid) {
        setError('Please enter a valid promotion code');
        return false;
      }
      setError(null);
      if (form.password && form.password.length < 6) {
        setPasswordError('Password must be at least 6 characters');
        return false;
      }
      if (form.password && form.password !== form.confirmPassword) {
        setPasswordError('Passwords do not match');
        return false;
      }
      setPasswordError('');
      return true;
    }

    // Standard path: phone verification required
    if (!phoneVerified) {
      setError('Please verify your phone number before submitting');
      return false;
    }
    
    if (form.password && form.password.length < 6) {
      setPasswordError('Password must be at least 6 characters');
      return false;
    }
    
    if (form.password && form.password !== form.confirmPassword) {
      setPasswordError('Passwords do not match');
      return false;
    }
    
    setPasswordError('');
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      const formData = new FormData();
      
      const isPromotion = paymentMethod === 'promotion';
      const phoneToSubmit = isPromotion
        ? `+91${form.phone}`
        : (verificationData ? verificationData.phone : `+91${form.phone}`);
      const transactionIdToSubmit = isPromotion ? 'PROMO' : form.transactionId;
      
      const { screenshot, promotionCode, ...formFields } = form;
      Object.entries(formFields).forEach(([key, value]) => {
        if (key === 'phone') {
          formData.append(key, phoneToSubmit);
        } else if (key === 'transactionId') {
          formData.append(key, transactionIdToSubmit);
        } else {
          formData.append(key, value);
        }
      });
      
      if (isPromotion) {
        formData.append('registrationMethod', 'promotion');
        formData.append('promotionCode', (form.promotionCode || '').trim());
      }
      
      if (screenshot && !isPromotion) {
        formData.append('screenshot', screenshot);
      }

      const response = await axios.post(API_ENDPOINTS.INFLUENCER, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      if (response.data) {
        // Send registration confirmation email
        try {
          await sendRegistrationConfirmation(response.data.influencer);
          console.log('✅ Registration confirmation email sent');
        } catch (emailError) {
          console.error('❌ Failed to send registration email:', emailError);
          // Don't fail the registration if email fails
        }
        
        setSuccess(true);
        setForm({
          name: '',
          phone: '',
          email: '',
          instagram: '',
          facebook: '',
          twitter: '',
          screenshot: null,
          transactionId: '',
          influencerType: 'Featured Influencer',
          password: '',
          confirmPassword: '',
          promotionCode: ''
        });
        setPreview(null);
        setPhoneVerified(false);
        setVerificationData(null);
        setPromoCodeValid(false);
        setPromoCodeTouched(false);
      }
    } catch (err) {
      if (err.response) {
        setError(err.response.data.error || 'Registration failed. Please try again.');
      } else if (err.request) {
        setError('Network error. Please check your connection.');
      } else {
        setError('An unexpected error occurred.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <AnimatePresence>
      <motion.div 
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-md p-4"
      >
        <motion.div 
          initial={{ opacity: 0, scale: 0.9, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.9, y: 20 }}
          transition={{ duration: 0.3, ease: "easeOut" }}
          className="bg-white rounded-2xl shadow-2xl w-full max-w-lg mx-2 flex flex-col border border-gray-100" 
          style={{ maxHeight: '95vh' }}
        >
        {/* Fixed Header */}
          <div className="bg-gradient-to-r from-blue-600 via-purple-600 to-indigo-600 p-6 text-white flex-shrink-0 rounded-t-2xl relative overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-r from-blue-500/20 to-purple-500/20"></div>
            <div className="relative flex justify-between items-center">
              <div>
                <h2 className="text-2xl font-bold mb-1">Join Our Creator Network</h2>
                <p className="text-white/80 text-sm">Start earning money with your content</p>
              </div>
              <motion.button 
              onClick={onClose} 
                className="text-white/80 hover:text-white text-2xl transition-colors duration-200 hover:bg-white/10 rounded-full p-2"
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
              >
                ✕
              </motion.button>
            </div>
        </div>

        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto p-6">
          <AnimatePresence>
          {error && (
              <motion.div 
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                className="bg-gradient-to-r from-red-50 to-red-100 border border-red-200 p-4 mb-6 rounded-xl"
              >
              <div className="flex items-start">
                  <div className="flex-shrink-0">
                    <svg className="h-5 w-5 text-red-500" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              </div>
                  <p className="ml-3 text-sm text-red-700 font-medium">{error}</p>
            </div>
              </motion.div>
          )}
          
          {success && (
              <motion.div 
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                className="bg-gradient-to-r from-green-50 to-green-100 border border-green-200 p-4 mb-6 rounded-xl"
              >
              <div className="flex items-start">
                  <div className="flex-shrink-0">
                    <svg className="h-5 w-5 text-green-500" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
              </div>
                  <p className="ml-3 text-sm text-green-700 font-medium">Registration successful!</p>
            </div>
              </motion.div>
          )}
          </AnimatePresence>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <div className="rounded-xl border-2 border-blue-200 bg-gradient-to-br from-blue-50 to-indigo-50 p-4">
                <p className="text-sm font-semibold text-gray-800">Registration — ₹199</p>
                <p className="mt-1 text-xs text-gray-600">Only for WhatsApp community members.</p>
              </div>
              <input type="hidden" name="influencerType" value="Featured Influencer" />
            </div>

            {/* Registration / Payment Method */}
            <div>
              <label className="block text-sm font-semibold text-gray-800 mb-3">Registration option</label>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <label className="flex items-center p-3 border-2 rounded-xl cursor-not-allowed bg-gray-50 border-gray-200 opacity-60">
                  <input
                    type="radio"
                    name="paymentMethod"
                    value="manual"
                    checked={false}
                    disabled
                    className="mr-3 h-4 w-4 text-gray-300 border-gray-200"
                  />
                  <span className="text-sm font-medium text-gray-400">UPI (Manual) — unavailable</span>
                </label>
                <motion.label 
                  className={`flex items-center p-3 border-2 rounded-xl cursor-pointer transition-all duration-200 ${
                    paymentMethod === 'razorpay' 
                      ? 'border-indigo-500 bg-indigo-50 shadow-md' 
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <input 
                    type="radio" 
                    name="paymentMethod" 
                    value="razorpay" 
                    checked={paymentMethod === 'razorpay'} 
                    onChange={() => setPaymentMethod('razorpay')} 
                    className="mr-3 h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300" 
                  />
                  <span className="text-sm font-medium">Razorpay</span>
                </motion.label>
                <motion.label 
                  className={`flex items-center p-3 border-2 rounded-xl cursor-pointer transition-all duration-200 ${
                    paymentMethod === 'promotion' 
                      ? 'border-emerald-500 bg-emerald-50 shadow-md' 
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <input 
                    type="radio" 
                    name="paymentMethod" 
                    value="promotion" 
                    checked={paymentMethod === 'promotion'} 
                    onChange={() => setPaymentMethod('promotion')} 
                    className="mr-3 h-4 w-4 text-emerald-600 focus:ring-emerald-500 border-gray-300" 
                  />
                  <span className="text-sm font-medium">Promotion (Free)</span>
                </motion.label>
              </div>
            </div>

            {/* Promotion code (only when Promotion selected) */}
            {paymentMethod === 'promotion' && (
              <div className="bg-gradient-to-br from-emerald-50 to-teal-50 p-5 rounded-2xl border-2 border-emerald-200">
                <label className="block text-sm font-semibold text-gray-800 mb-2">
                  Promotion code <span className="text-red-500">*</span>
                </label>
                <input
                  name="promotionCode"
                  value={form.promotionCode}
                  onChange={handleChange}
                  onBlur={() => setPromoCodeTouched(true)}
                  placeholder="Enter your promotion code"
                  className={`w-full px-4 py-3 text-sm border-2 rounded-xl font-medium uppercase tracking-wide placeholder:normal-case placeholder:tracking-normal focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-all ${
                    promoCodeTouched && !promoCodeValid && form.promotionCode.trim()
                      ? 'border-red-300 bg-red-50/50'
                      : promoCodeValid
                        ? 'border-emerald-400 bg-emerald-50/50'
                        : 'border-gray-200 bg-white'
                  }`}
                />
                {promoCodeValid && (
                  <p className="mt-2 text-sm text-emerald-700 font-medium flex items-center gap-2">
                    <span className="inline-flex w-5 h-5 rounded-full bg-emerald-500 text-white items-center justify-center text-xs">✓</span>
                    Valid code — free registration, no payment or phone verification required.
                  </p>
                )}
                {promoCodeTouched && form.promotionCode.trim() && !promoCodeValid && (
                  <p className="mt-2 text-sm text-red-600">Invalid promotion code. Please check and try again.</p>
                )}
              </div>
            )}

            {paymentMethod !== 'promotion' && (
            <div className="bg-gradient-to-br from-blue-50 via-purple-50 to-indigo-50 p-6 rounded-2xl border border-gray-200">
              <div className="text-center">
                <div className="inline-flex items-center px-4 py-2 rounded-full bg-gradient-to-r from-blue-100 to-purple-100 text-blue-800 mb-4 text-sm font-semibold">
                  <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M8.433 7.418c.155-.103.346-.196.567-.267v1.698a2.305 2.305 0 01-.567-.267C8.07 8.34 8 8.114 8 8c0-.114.07-.34.433-.582zM11 12.849v-1.698c.22.071.412.164.567.267.364.243.433.468.433.582 0 .114-.07.34-.433.582a2.305 2.305 0 01-.567.267z" />
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-13a1 1 0 10-2 0v.092a4.535 4.535 0 00-1.676.662C6.602 6.234 6 7.009 6 8c0 .99.602 1.765 1.324 2.246.48.32 1.054.545 1.676.662v1.941c-.391-.127-.68-.317-.843-.504a1 1 0 10-1.51 1.31c.562.649 1.413 1.076 2.353 1.253V15a1 1 0 102 0v-.092a4.535 4.535 0 001.676-.662C13.398 13.766 14 12.991 14 12c0-.99-.602-1.765-1.324-2.246A4.535 4.535 0 0011 9.092V7.151c.391.127.68.317.843.504a1 1 0 101.511-1.31c-.563-.649-1.413-1.076-2.354-1.253V5z" clipRule="evenodd" />
                  </svg>
                  Payment Required
                </div>

                <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200 mb-4">
                  {paymentMethod === 'manual' ? (
                    <>
                      <img 
                        src={`https://api.qrserver.com/v1/create-qr-code/?size=150x150&data=upi://pay?pa=your-upi-id&am=${price}`} 
                        alt="Payment QR" 
                        className="w-36 h-36 mx-auto mb-3" 
                      />
                      <div className="space-y-1 text-xs">
                        <div className="flex justify-between">
                          <span className="text-gray-500">UPI ID:</span>
                          <span className="font-medium">your-upi-id@upi</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-500">Amount:</span>
                          <span className="font-medium">₹{price}</span>
                        </div>
                      </div>
                      <p className="text-xs text-gray-500 mt-2">Scan the QR code or use this UPI ID to complete payment</p>
                    </>
                  ) : (
                    <>
                      <div className="text-sm font-medium mb-2">Online payment via Razorpay</div>
                      <div className="flex flex-col items-center">
                        <motion.button
                          type="button"
                          onClick={handleRazorpayPayment}
                          disabled={paymentLoading}
                          className={`px-6 py-3 rounded-xl bg-gradient-to-r from-indigo-600 to-purple-600 text-white font-semibold shadow-lg hover:shadow-xl transition-all duration-300 ${
                            paymentLoading ? 'opacity-70 cursor-not-allowed' : 'hover:from-indigo-700 hover:to-purple-700 transform hover:-translate-y-0.5'
                          }`}
                          whileHover={!paymentLoading ? { scale: 1.05 } : {}}
                          whileTap={!paymentLoading ? { scale: 0.95 } : {}}
                        >
                          {paymentLoading ? (
                            <div className="flex items-center">
                              <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                              </svg>
                              Processing...
                            </div>
                          ) : (
                            `Pay ₹${price} with Razorpay`
                          )}
                        </motion.button>
                        {paymentError && <p className="text-xs text-red-600 mt-2">{paymentError}</p>}
                        {razorpayOrderId && (
                          <p className="text-xs text-green-600 mt-2">Order ID: {razorpayOrderId}</p>
                        )}
                        <p className="text-xs text-gray-500 mt-2">Secure payment gateway. Transaction ID will be set automatically after successful payment.</p>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
            )}

            <div>
              <label className="block text-sm font-semibold text-gray-800 mb-2">
                Full Name <span className="text-red-500">*</span>
              </label>
              <input
                name="name"
                value={form.name}
                onChange={handleChange}
                required
                className="w-full px-4 py-3 text-sm border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 bg-gray-50 focus:bg-white"
                placeholder="John Doe"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-800 mb-2">
                Phone Number <span className="text-red-500">*</span>
                {phoneVerified && (
                  <span className="ml-2 inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                    ✓ Verified
                  </span>
                )}
              </label>
              <div className="space-y-3">
                <div className="flex rounded-xl shadow-sm">
                  <span className="inline-flex items-center px-4 rounded-l-xl border-2 border-r-0 border-gray-200 bg-gradient-to-r from-gray-100 to-gray-50 text-gray-600 text-sm font-medium">
                    +91
                  </span>
                  <input
                    name="phone"
                    value={form.phone}
                    onChange={handleChange}
                    required
                    type="tel"
                    className="flex-1 min-w-0 block w-full px-4 py-3 rounded-none rounded-r-xl border-2 border-gray-200 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm transition-all duration-200 bg-gray-50 focus:bg-white"
                    placeholder="9876543210"
                    maxLength="10"
                    disabled={phoneVerified}
                  />
                </div>
                
                <p className="text-xs text-gray-500 text-center">
                  Enter your 10-digit mobile number (country code +91 will be added automatically)
                </p>
                
                {paymentMethod !== 'promotion' && !phoneVerified ? (
                  <motion.button
                    type="button"
                    onClick={handleVerifyPhone}
                    disabled={!form.phone || form.phone.length < 10}
                    className={`w-full py-2 px-4 rounded-lg font-medium transition-all duration-200 ${
                      !form.phone || form.phone.length < 10
                        ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                        : 'bg-blue-600 text-white hover:bg-blue-700 shadow-md hover:shadow-lg'
                    }`}
                    whileHover={(!form.phone || form.phone.length < 10) ? {} : { scale: 1.02 }}
                    whileTap={(!form.phone || form.phone.length < 10) ? {} : { scale: 0.98 }}
                  >
                    📱 Verify Phone Number
                  </motion.button>
                ) : paymentMethod !== 'promotion' && phoneVerified ? (
                  <div className="flex items-center justify-center py-2 px-4 rounded-lg bg-green-50 border border-green-200">
                    <svg className="w-5 h-5 text-green-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <span className="text-green-700 font-medium text-sm">Phone number verified successfully</span>
                  </div>
                ) : null}
              </div>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-800 mb-2">
                Email Address <span className="text-red-500">*</span>
              </label>
              <input
                name="email"
                value={form.email}
                onChange={handleChange}
                required
                type="email"
                autoComplete="username"
                className="w-full px-4 py-3 text-sm border-2 border-gray-200 rounded-xl text-gray-900 bg-gray-50 focus:bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 placeholder:text-gray-500"
                placeholder="john@example.com"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-800 mb-2">
                Password (min 6 chars) <span className="text-gray-400">(Optional - you can set this later)</span>
              </label>
              <input
                name="password"
                type="password"
                value={form.password}
                onChange={handleChange}
                minLength={6}
                autoComplete="new-password"
                className="w-full px-4 py-3 text-sm border-2 border-gray-200 rounded-xl text-gray-900 bg-gray-50 focus:bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 placeholder:text-gray-500"
                placeholder="•••••• (Optional)"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-800 mb-2">
                Confirm Password <span className="text-gray-400">(Optional)</span>
              </label>
              <input
                name="confirmPassword"
                type="password"
                value={form.confirmPassword}
                onChange={handleChange}
                autoComplete="new-password"
                className="w-full px-4 py-3 text-sm border-2 border-gray-200 rounded-xl text-gray-900 bg-gray-50 focus:bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 placeholder:text-gray-500"
                placeholder="•••••• (Optional)"
              />
              {passwordError && (
                <motion.p 
                  initial={{ opacity: 0, y: -5 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="mt-2 text-sm text-red-600 font-medium"
                >
                  {passwordError}
                </motion.p>
              )}
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-800 mb-2">
                Instagram Handle <span className="text-red-500">*</span>
              </label>
              <div className="flex rounded-xl shadow-sm">
                <span className="inline-flex items-center px-4 rounded-l-xl border-2 border-r-0 border-gray-200 bg-gradient-to-r from-pink-100 to-purple-100 text-gray-600 text-sm font-medium">
                  instagram.com/
                </span>
                <input
                  name="instagram"
                  value={form.instagram}
                  onChange={handleChange}
                  required
                  className="flex-1 min-w-0 block w-full px-4 py-3 rounded-none rounded-r-xl border-2 border-gray-200 text-gray-900 bg-gray-50 focus:bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm transition-all duration-200 placeholder:text-gray-500"
                  placeholder="yourusername"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-800 mb-2">
                Facebook URL <span className="text-gray-400">(Optional)</span>
              </label>
              <div className="flex rounded-xl shadow-sm">
                <span className="inline-flex items-center px-4 rounded-l-xl border-2 border-r-0 border-gray-200 bg-gradient-to-r from-blue-100 to-indigo-100 text-gray-600 text-sm font-medium">
                  facebook.com/
                </span>
                <input
                  name="facebook"
                  value={form.facebook}
                  onChange={handleChange}
                  className="flex-1 min-w-0 block w-full px-4 py-3 rounded-none rounded-r-xl border-2 border-gray-200 text-gray-900 bg-gray-50 focus:bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm transition-all duration-200 placeholder:text-gray-500"
                  placeholder="yourusername"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-800 mb-2">
                X (Twitter) Handle <span className="text-gray-400">(Optional)</span>
              </label>
              <div className="flex rounded-xl shadow-sm">
                <span className="inline-flex items-center px-4 rounded-l-xl border-2 border-r-0 border-gray-200 bg-gradient-to-r from-gray-100 to-gray-200 text-gray-600 text-sm font-medium">
                  x.com/
                </span>
                <input
                  name="twitter"
                  value={form.twitter}
                  onChange={handleChange}
                  className="flex-1 min-w-0 block w-full px-4 py-3 rounded-none rounded-r-xl border-2 border-gray-200 text-gray-900 bg-gray-50 focus:bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm transition-all duration-200 placeholder:text-gray-500"
                  placeholder="yourusername"
                />
              </div>
            </div>

            {paymentMethod !== 'promotion' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Payment Screenshot 
                {paymentMethod === 'manual' && <span className="text-red-500">*</span>}
                {paymentMethod === 'razorpay' && <span className="text-gray-400 text-xs ml-2">(Not required for online payment)</span>}
              </label>
              <div className="mt-1">
                <label className="cursor-pointer block">
                  <div className="group relative">
                    <div className={`flex flex-col items-center justify-center px-4 py-6 border-2 border-dashed rounded-lg transition ${
                      paymentMethod === 'razorpay' 
                        ? 'border-gray-200 bg-gray-50 cursor-not-allowed' 
                        : 'border-gray-300 hover:border-indigo-500'
                    }`}>
                      <svg className={`mx-auto h-10 w-10 ${paymentMethod === 'razorpay' ? 'text-gray-300' : 'text-gray-400 group-hover:text-indigo-500'}`} stroke="currentColor" fill="none" viewBox="0 0 48 48" aria-hidden="true">
                        <path d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                      <p className="mt-1 text-xs text-gray-600 text-center">
                        {paymentMethod === 'razorpay' ? (
                          <>
                            <span className="font-medium text-gray-400">Not required for Razorpay</span><br/>
                            <span className="text-gray-400">Payment is handled online</span>
                          </>
                        ) : (
                          <>
                            <span className="font-medium text-indigo-600 hover:text-indigo-500">Click to upload</span><br/>
                            PNG, JPG up to 2MB
                          </>
                        )}
                      </p>
                    </div>

                    <input
                      name="screenshot"
                      type="file"
                      accept="image/*"
                      onChange={handleChange}
                      required={paymentMethod === 'manual'}
                      disabled={paymentMethod === 'razorpay'}
                      className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                    />
                  </div>
                </label>
              </div>
              {preview && (
                <div className="mt-2">
                  <img
                    src={preview}
                    alt="Payment preview"
                    className="h-24 w-auto rounded border border-gray-200 object-contain mx-auto"
                  />
                </div>
              )}
            </div>
            )}

            {paymentMethod !== 'promotion' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Transaction ID 
                {paymentMethod === 'manual' && <span className="text-red-500">*</span>}
                {paymentMethod === 'razorpay' && <span className="text-gray-400 text-xs ml-2">(Auto-filled after payment)</span>}
              </label>
              <input
                name="transactionId"
                value={form.transactionId}
                onChange={handleChange}
                required={paymentMethod === 'manual'}
                disabled={paymentMethod === 'razorpay' && !!form.transactionId}
                className={`w-full px-3 py-2 text-sm border border-gray-300 rounded-lg text-gray-900 bg-white focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none placeholder:text-gray-500 ${
                  paymentMethod === 'razorpay' && !!form.transactionId ? 'bg-gray-50 cursor-not-allowed' : ''
                }`}
                placeholder={paymentMethod === 'razorpay' ? 'Will be set automatically after payment' : 'UPI1234567890 or merchantOrderId (for online)'}
              />
            </div>
            )}

            <div className="pt-4 pb-2">
              {(() => {
                const isPromo = paymentMethod === 'promotion';
                const canSubmit = isPromo ? promoCodeValid : phoneVerified;
                return (
                  <>
                    <motion.button
                      type="submit"
                      disabled={loading || !canSubmit}
                      className={`w-full flex justify-center items-center py-4 px-6 border border-transparent rounded-xl shadow-lg text-base font-semibold text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-all duration-300 ${
                        isPromo
                          ? 'bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700'
                          : 'bg-gradient-to-r from-blue-600 via-purple-600 to-indigo-600 hover:from-blue-700 hover:via-purple-700 hover:to-indigo-700'
                      } ${loading || !canSubmit ? 'opacity-70 cursor-not-allowed' : 'hover:shadow-xl transform hover:-translate-y-0.5'}`}
                      whileHover={(!loading && canSubmit) ? { scale: 1.02 } : {}}
                      whileTap={(!loading && canSubmit) ? { scale: 0.98 } : {}}
                    >
                      {loading ? (
                        <div className="flex items-center">
                          <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          Processing Registration...
                        </div>
                      ) : !canSubmit ? (
                        <div className="flex items-center">
                          <span>{isPromo ? '✨ Enter a valid promotion code above' : '📱 Verify Phone Number First'}</span>
                        </div>
                      ) : (
                        <div className="flex items-center">
                          <span>{isPromo ? '🎉 Register free with code' : '🚀 Register as Influencer'}</span>
                        </div>
                      )}
                    </motion.button>
                    {!isPromo && !phoneVerified && (
                      <p className="mt-2 text-sm text-center text-gray-500">
                        Please verify your phone number before submitting the form
                      </p>
                    )}
                  </>
                );
              })()}
            </div>
          </form>
        </div>
      </motion.div>
      
      {/* OTP Verification Modal (SMS) */}
      <AnimatePresence>
        {showOTPVerification && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-60 flex items-center justify-center bg-black/70 backdrop-blur-md p-4"
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              transition={{ duration: 0.3, ease: "easeOut" }}
              className="relative bg-white rounded-xl p-6 w-full max-w-md shadow-2xl"
            >
              <button
                onClick={() => setShowOTPVerification(false)}
                className="absolute -top-4 -right-4 bg-white rounded-full p-2 shadow-lg hover:shadow-xl transition-shadow z-10"
              >
                <svg className="w-6 h-6 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
              <div className="text-center mb-4">
                <h3 className="text-lg font-semibold text-gray-800">Verify Phone Number</h3>
                <p className="text-sm text-gray-500">Code will be sent to +91{form.phone}</p>
              </div>
              {otpMessage && (
                <div className="mb-3 text-sm text-center text-red-600">{otpMessage}</div>
              )}
              <div className="flex gap-2 mb-3">
                <button
                  type="button"
                  onClick={sendSmsOtp}
                  disabled={otpLoading}
                  className={`flex-1 py-2 px-3 rounded-lg text-white ${otpLoading ? 'bg-gray-400' : 'bg-blue-600 hover:bg-blue-700'}`}
                >
                  {otpLoading ? 'Sending...' : 'Send OTP'}
                </button>
              </div>
              <div className="mb-3">
                <input
                  value={otp}
                  onChange={(e)=>setOtp(e.target.value)}
                  maxLength={6}
                  placeholder="Enter 6-digit code"
                  className="w-full px-4 py-3 text-sm border-2 border-gray-200 rounded-xl text-gray-900 bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none placeholder:text-gray-500"
                />
              </div>
              <button
                type="button"
                onClick={verifySmsOtp}
                disabled={otpLoading || otp.length !== 6}
                className={`w-full py-2 px-3 rounded-lg text-white ${otpLoading || otp.length !== 6 ? 'bg-gray-400' : 'bg-green-600 hover:bg-green-700'}`}
              >
                {otpLoading ? 'Verifying...' : 'Verify'}
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
    </AnimatePresence>
  );
};

export default RegisterInfluencer;